import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Tag, Modal, message, 
  Input, Select, Form, Tabs, Card, Statistic 
} from 'antd';
import { 
  EditOutlined, DeleteOutlined, // // EyeOutlined, // Not used // Not used
  BarChartOutlined, CheckCircleOutlined, 
  // // DollarOutlined // Not used // Not used
} from '@ant-design/icons';
import axios from 'axios';
import apiService from '../services/apiService';

const { Search } = Input;
const { TabPane } = Tabs;

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [pendingSponsors, setPendingSponsors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isStatsModalVisible, setIsStatsModalVisible] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [sponsorStats, setSponsorStats] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    level: undefined,
    sortBy: 'createdAt',
    order: 'desc'
  });

  const fetchSponsors = async (params = {}) => {
    setLoading(true);
    try {
      const { current, pageSize } = params;
      const { search, level, sortBy, order } = filters;
      
      let url = `${process.env.REACT_APP_API_URL}/admin/sponsor?page=${current}&limit=${pageSize}&sortBy=${sortBy}&order=${order}`;
      if (search) url += `&search=${search}`;
      if (level) url += `&level=${level}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setSponsors(response.data.sponsors);
      setPagination({
        ...pagination,
        total: response.data.total,
        current: response.data.currentPage
      });
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des sponsors');
    }
    setLoading(false);
  };

  const fetchPendingSponsors = async () => {
    try {
      const response = await axios.get(
        apiService.admin.getPendingSponsors(),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setPendingSponsors(response.data);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des demandes en attente');
    }
  };

  const fetchSponsorStats = async (sponsorId) => {
    try {
      const response = await axios.get(
        apiService.admin.getSponsorStats(sponsorId),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setSponsorStats(response.data);
      setIsStatsModalVisible(true);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des statistiques');
    }
  };

  useEffect(() => {
    fetchSponsors({ current: 1, pageSize: 10 });
    fetchPendingSponsors();
  }, [filters]);

  const handleTableChange = (newPagination, filters, sorter) => {
    const newFilters = { ...filters };
    if (sorter.field && sorter.order) {
      newFilters.sortBy = sorter.field;
      newFilters.order = sorter.order === 'descend' ? 'desc' : 'asc';
    }
    setFilters(newFilters);
    fetchSponsors(newPagination);
  };

  const handleDelete = async (sponsorId) => {
    try {
      await axios.delete(
        apiService.admin.getSponsorById(sponsorId),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Sponsor supprimé avec succès');
      fetchSponsors(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la suppression');
    }
  };

  const handleValidatePending = async (sponsorId, approved) => {
    try {
      await axios.post(
        apiService.admin.validateSponsor(sponsorId),
        { approved },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success(approved ? 'Sponsor validé avec succès' : 'Demande rejetée');
      fetchPendingSponsors();
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la validation');
    }
  };

  const showModal = (sponsor = null) => {
    setSelectedSponsor(sponsor);
    if (sponsor) {
      form.setFieldsValue({
        ...sponsor,
        ...sponsor.user
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleModalSubmit = async (values) => {
    try {
      await axios.put(
        apiService.admin.getSponsorById(selectedSponsor._id),
        values,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Sponsor mis à jour avec succès');
      setIsModalVisible(false);
      fetchSponsors(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de l\'enregistrement');
    }
  };

  const columns = [
    {
      title: 'Société',
      dataIndex: 'company',
      key: 'company',
      sorter: true,
    },
    {
      title: 'Niveau',
      dataIndex: 'currentLevel',
      key: 'level',
      render: (level) => {
        if (!level) return <Tag>NON DÉFINI</Tag>;
        
        const levelColors = {
          gold: 'gold',
          silver: 'default',
          bronze: 'brown'
        };

        return (
          <Tag color={levelColors[level] || 'default'}>
            {level.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: ['user', 'email'],
      key: 'email',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<BarChartOutlined />}
            onClick={() => fetchSponsorStats(record._id)}
            className="hover:text-primary-600"
          />
          <Button 
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            className="hover:text-primary-600"
          />
          <Button 
            icon={<DeleteOutlined />}
            danger
            onClick={() => {
              Modal.confirm({
                title: 'Êtes-vous sûr de vouloir supprimer ce sponsor ?',
                content: 'Cette action est irréversible.',
                okText: 'Oui',
                cancelText: 'Non',
                onOk: () => handleDelete(record._id)
              });
            }}
          />
        </Space>
      ),
    },
  ];

  const pendingColumns = [
    {
      title: 'Société',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => handleValidatePending(record._id, true)}
          >
            Valider
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleValidatePending(record._id, false)}
          >
            Rejeter
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Gestion des Sponsors</h1>
      </div>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Sponsors actifs" key="1">
          <div className="mb-4 flex gap-4">
            <Search
              placeholder="Rechercher un sponsor"
              onSearch={(value) => setFilters({ ...filters, search: value })}
              style={{ width: 300 }}
            />
            <Select
              placeholder="Niveau de sponsoring"
              style={{ width: 200 }}
              allowClear
              onChange={(value) => setFilters({ ...filters, level: value })}
            >
              <Select.Option value="gold">Gold</Select.Option>
              <Select.Option value="silver">Silver</Select.Option>
              <Select.Option value="bronze">Bronze</Select.Option>
            </Select>
          </div>

          <Table
            columns={columns}
            dataSource={sponsors}
            rowKey="_id"
            pagination={pagination}
            loading={loading}
            onChange={handleTableChange}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              Demandes en attente
              {pendingSponsors.length > 0 && (
                <Tag className="ml-2">{pendingSponsors.length}</Tag>
              )}
            </span>
          } 
          key="2"
        >
          <Table
            columns={pendingColumns}
            dataSource={pendingSponsors}
            rowKey="_id"
            pagination={false}
          />
        </TabPane>
      </Tabs>

      <Modal
        title={selectedSponsor ? 'Modifier le sponsor' : 'Détails du sponsor'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
        >
          <Form.Item
            name="company"
            label="Société"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="language"
            label="Langue"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="fr">Français</Select.Option>
              <Select.Option value="en">Anglais</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="phone"
            label="Téléphone"
          >
            <Input />
          </Form.Item>

          <div className="flex justify-end gap-3">
            <Button onClick={() => setIsModalVisible(false)}>
              Annuler
            </Button>
            <Button type="primary" htmlType="submit">
              Enregistrer
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Statistiques du sponsor"
        open={isStatsModalVisible}
        onCancel={() => setIsStatsModalVisible(false)}
        footer={null}
        width={800}
      >
        {sponsorStats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <Statistic
                  title="Total investi"
                  value={sponsorStats.totalInvested || 0}
                  prefix="€"
                  precision={2}
                />
              </Card>
              <Card>
                <Statistic
                  title="Nombre d'événements"
                  value={sponsorStats.totalEvents || 0}
                />
              </Card>
            </div>

            <Card title="Derniers paiements">
              <Table
                dataSource={sponsorStats.recentPayments || []}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'createdAt',
                    render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
                  },
                  {
                    title: 'Pack',
                    dataIndex: ['pack', 'name'],
                    render: (name) => name || 'N/A',
                  },
                  {
                    title: 'Montant',
                    dataIndex: 'amount',
                    render: (amount) => amount ? `${amount} €` : '0 €',
                  },
                ]}
                pagination={false}
              />
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Sponsors; 